import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Numeric pill for unread counts. Defaults to destructive tone (red) for
 * notification badges. Overflows to `${max}+` when count exceeds max (default 99).
 */
const countBadgeVariants = cva(
  'inline-flex items-center justify-center rounded-full min-w-[16px] h-4 px-1 text-[10px] font-bold leading-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
        subtle: 'bg-muted text-foreground border border-border',
      },
    },
    defaultVariants: {
      variant: 'destructive',
    },
  },
)

export interface CountBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>, VariantProps<typeof countBadgeVariants> {
  count: number
  /** Overflow threshold; counts above this render as `${max}+`. Default 99. */
  max?: number
}

const CountBadge = React.forwardRef<HTMLSpanElement, CountBadgeProps>(
  ({ className, variant, count, max = 99, 'aria-label': ariaLabel, ...props }, ref) => {
    const display = count > max ? `${max}+` : String(count)
    return (
      <span
        ref={ref}
        className={cn(countBadgeVariants({ variant }), className)}
        aria-label={ariaLabel ?? `${count} unread`}
        {...props}
      >
        {display}
      </span>
    )
  },
)
CountBadge.displayName = 'CountBadge'

export { CountBadge, countBadgeVariants }
